import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import RoleRoute from './components/RoleRoute';
import AppLayout from './components/AppLayout';
import { ToastProvider } from './components/ui/Toast';
import ErrorBoundary from './components/ui/ErrorBoundary';

import LoginPage from './pages/LoginPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import RejectedPage from './pages/RejectedPage';
import EPRDashboard from './pages/EPRDashboard';
import EPRSetupPage from './pages/EPRSetupPage';
import TemplateBuilder from './pages/TemplateBuilder';
import CycleManagement from './pages/CycleManagement';
import SelfReviewForm from './pages/SelfReviewForm';
import Review360Form from './pages/Review360Form';
import ManagerReviewForm from './pages/ManagerReviewForm';
import CalibrationPanel from './pages/CalibrationPanel';
import ApprovalPanel from './pages/ApprovalPanel';
import EPRReports from './pages/EPRReports';
import LeaderReviewPage from './pages/LeaderReviewPage';
import LeaderReviewSelf from './pages/LeaderReviewSelf';
import LeaderReviewPeers from './pages/LeaderReviewPeers';
import LeaderReviewLive from './pages/LeaderReviewLive';
import LeaderReviewResults from './pages/LeaderReviewResults';
import MagicLinkReviewForm from './pages/MagicLinkReviewForm';
import MagicLinkRoute from './pages/MagicLinkRoute';
import HrV1Console from './pages/HrV1Console';
import HrV1LaunchEpr from './pages/HrV1LaunchEpr';
import HrV1CycleIndex from './pages/HrV1CycleIndex';
import HrV1ReviewsIndex from './pages/HrV1ReviewsIndex';
import HrV1AppraisalsIndex from './pages/HrV1AppraisalsIndex';
import HrV1AppealsIndex from './pages/HrV1AppealsIndex';
import HrV1CalibrationIndex from './pages/HrV1CalibrationIndex';
import MagicLinkDispatcher from './pages/MagicLinkDispatcher';
import ReviewsIndex from './pages/ReviewsIndex';
import MonitorReviewForm from './pages/MonitorReviewForm';

const HR_ROLES      = ['superadmin', 'admin', 'hr_admin'];
const LEADER_ROLES  = ['superadmin', 'admin', 'hr_admin', 'leader'];
const ALL_ROLES     = ['superadmin', 'admin', 'hr_admin', 'leader', 'employee'];

// Inner component — must be inside AuthProvider to call useAuth()
const AppRoutes: React.FC = () => {
  const { isPendingApproval, isRejected } = useAuth();

  if (isPendingApproval) return <PendingApprovalPage />;
  if (isRejected) return <RejectedPage />;

  return (
    <Routes>
      {/* Public routes — no login */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/360/:token" element={<Review360Form />} />
      <Route path="/r/:token" element={<MagicLinkReviewForm />} />
      <Route path="/m/:token" element={<MagicLinkRoute />} />

        {/* Authenticated routes (with NavBar) */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <AppLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<EPRDashboard />} />

                  {/* Employee */}
                  <Route path="/self-review" element={<RoleRoute allowedRoles={ALL_ROLES}><SelfReviewForm /></RoleRoute>} />

                  {/* Leader Quick Review */}
                  <Route path="/leader-epr" element={<Navigate to="/leader-review" replace />} />

                  {/* Manager */}
                  <Route
                    path="/manager-review"
                    element={
                      <RoleRoute allowedRoles={LEADER_ROLES}>
                        <ManagerReviewForm />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/manager-review/:reviewFormId"
                    element={
                      <RoleRoute allowedRoles={LEADER_ROLES}>
                        <ManagerReviewForm />
                      </RoleRoute>
                    }
                  />

                  {/* Reviews — index + start + monitor (HR-friendly IA) */}
                  <Route
                    path="/reviews"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <ReviewsIndex />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/reviews/new"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <MagicLinkDispatcher />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/reviews/:formId"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <MonitorReviewForm />
                      </RoleRoute>
                    }
                  />

                  {/* HR Admin (legacy /dispatch alias kept) */}
                  <Route
                    path="/dispatch"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <MagicLinkDispatcher />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/setup"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <EPRSetupPage />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/templates"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <TemplateBuilder />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/templates/new"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <TemplateBuilder />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/templates/:id"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <TemplateBuilder />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/cycles"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <CycleManagement />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/calibration"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <CalibrationPanel />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/calibration/:cycleId"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <CalibrationPanel />
                      </RoleRoute>
                    }
                  />

                  {/* Approval */}
                  <Route
                    path="/approval"
                    element={
                      <RoleRoute allowedRoles={LEADER_ROLES}>
                        <ApprovalPanel />
                      </RoleRoute>
                    }
                  />

                  {/* Reports */}
                  <Route
                    path="/reports"
                    element={
                      <RoleRoute allowedRoles={LEADER_ROLES}>
                        <EPRReports />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/reports/:cycleId"
                    element={
                      <RoleRoute allowedRoles={LEADER_ROLES}>
                        <EPRReports />
                      </RoleRoute>
                    }
                  />

                  {/* HR Framework (hr_v1) — index + appraisals + per-cycle console */}
                  <Route
                    path="/hr-v1"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1CycleIndex />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/hr-v1/appraisals"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1AppraisalsIndex />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/hr-v1/appeals"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1AppealsIndex />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/hr-v1/reviews"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1ReviewsIndex />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/hr-v1/calibration"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1CalibrationIndex />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/hr-v1/:cycleId/launch/epr"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1LaunchEpr />
                      </RoleRoute>
                    }
                  />
                  <Route
                    path="/hr-v1/:cycleId"
                    element={
                      <RoleRoute allowedRoles={HR_ROLES}>
                        <HrV1Console />
                      </RoleRoute>
                    }
                  />

                  {/* Leader EPR Review — unified module */}
                  <Route path="/leader-review" element={<RoleRoute allowedRoles={LEADER_ROLES}><LeaderReviewPage /></RoleRoute>} />
                  <Route path="/leader-review/self/:sessionId" element={<RoleRoute allowedRoles={LEADER_ROLES}><LeaderReviewSelf /></RoleRoute>} />
                  <Route path="/leader-review/peers/:sessionId" element={<RoleRoute allowedRoles={LEADER_ROLES}><LeaderReviewPeers /></RoleRoute>} />
                  <Route path="/leader-review/live/:sessionId" element={<RoleRoute allowedRoles={LEADER_ROLES}><LeaderReviewLive /></RoleRoute>} />
                  <Route path="/leader-review/results/:sessionId" element={<RoleRoute allowedRoles={LEADER_ROLES}><LeaderReviewResults /></RoleRoute>} />

                  {/* Catch-all */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
            </PrivateRoute>
          }
        />
      </Routes>
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
  <AuthProvider>
    <ToastProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      {/* Global screen-reader announcement region — see hooks/useAnnounce.ts */}
      <div
        id="live-region"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
    </ToastProvider>
  </AuthProvider>
  </ErrorBoundary>
);

export default App;
